import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button';
import { ChevronLeft } from 'lucide-react';
import type {
  BuilderSection,
  WebsiteBuilderPage,
  WebsiteBuilderState,
} from 'types/websiteBuilder';
import { ROUTES } from 'utils/constants';
import {
  BUILDER_STORAGE_KEY,
  createDefaultWebsiteBuilderState,
  createSection,
} from '../builderTemplates';
import { syncHeaderNavigationWithPages } from '../pageNavSync';
import ActiveSectionsPanel from '../ActiveSectionsPanel/ActiveSectionsPanel';
import Canvas from '../Canvas';
import styles from '../WebsiteBuilder.module.scss';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import { compressImageFile } from 'utils/imageUtils';

const SIDEBAR_WIDTH_KEY = 'website-builder-page-sidebar-width';
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 360;
const SIDEBAR_DEFAULT = 240;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function loadNumber(
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return clamp(n, min, max);
  } catch {
    return fallback;
  }
}

function saveNumber(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

const DEFAULT_BUILDER_STATE: WebsiteBuilderState =
  createDefaultWebsiteBuilderState();

function isWebsiteBuilderState(x: unknown): x is WebsiteBuilderState {
  if (typeof x !== 'object' || x == null) return false;
  const r = x as Record<string, unknown>;
  return Array.isArray(r.sections);
}

function loadFromStorage(): WebsiteBuilderState {
  try {
    const raw = localStorage.getItem(BUILDER_STORAGE_KEY);
    if (!raw) return DEFAULT_BUILDER_STATE;
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return syncHeaderNavigationWithPages({
        ...DEFAULT_BUILDER_STATE,
        sections: parsed as BuilderSection[],
      });
    }
    if (isWebsiteBuilderState(parsed)) {
      return syncHeaderNavigationWithPages({
        sections: Array.isArray(parsed.sections) ? parsed.sections : [],
        pageBackgroundUrl:
          typeof parsed.pageBackgroundUrl === 'string'
            ? parsed.pageBackgroundUrl
            : '',
        companyDomain:
          typeof parsed.companyDomain === 'string' ? parsed.companyDomain : '',
        pages: Array.isArray(parsed.pages) ? parsed.pages : [],
      });
    }
    return DEFAULT_BUILDER_STATE;
  } catch {
    return DEFAULT_BUILDER_STATE;
  }
}

function saveToStorage(state: WebsiteBuilderState) {
  try {
    localStorage.setItem(
      BUILDER_STORAGE_KEY,
      JSON.stringify(syncHeaderNavigationWithPages(state)),
    );
  } catch {
    // ignore
  }
}

function ensureBodySections(page: WebsiteBuilderPage): BuilderSection[] {
  const existing = Array.isArray(page.bodySections) ? page.bodySections : [];
  if (existing.length > 0) return existing;
  if (page.id === 'page-default-donate') {
    const donateSection = createSection('body', 0);
    donateSection.data = {
      ...donateSection.data,
      variant: 'donate',
      title: 'Support Our Ministry',
      subtitle: 'We need your help.',
      body: 'Your giving helps us continue outreach, discipleship, and community care.',
      donateImageUrl: '',
      donateSuggestedAmounts: '100,250,500,1000',
      donateBankOptions: 'GCash,BDO,UnionBank',
      donateMode: 'one-time',
      donateButtonLabel: 'Give Now',
      donateNote: 'Secure online giving. Thank you for your generosity.',
      thankYouTitle: 'Thank you for your gift!',
      thankYouSubtitle: 'Your donation will help local wildlife thrive.',
      thankYouReceiptText:
        "We've sent your donation receipt to [donor email address]",
      thankYouShareFacebookLabel: 'Share on Facebook',
      thankYouShareInstagramLabel: 'Share on Instagram',
    };
    return [donateSection];
  }
  return [createSection('body', 0)];
}

function getSharedSections(sections: BuilderSection[]) {
  const header = sections.find((s) => s.type === 'header');
  const cta = sections.find((s) => s.type === 'cta');
  const footer = sections.find((s) => s.type === 'footer');
  return { header, cta, footer };
}

function PageBuilder() {
  const navigate = useNavigate();
  const { pageId } = useParams();

  const [builderState, setBuilderState] = useState<WebsiteBuilderState>(
    DEFAULT_BUILDER_STATE,
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [previewMode, setPreviewMode] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [sidebarWidth, setSidebarWidth] = useState(() =>
    loadNumber(SIDEBAR_WIDTH_KEY, SIDEBAR_DEFAULT, SIDEBAR_MIN, SIDEBAR_MAX),
  );

  const [resizingTarget, setResizingTarget] = useState<'sidebar' | null>(null);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const latestSidebarWidthRef = useRef(sidebarWidth);
  latestSidebarWidthRef.current = sidebarWidth;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updatePageBackgroundUrl = useCallback((url: string) => {
    setBuilderState((prev) => {
      const next = { ...prev, pageBackgroundUrl: url };
      saveToStorage(next);
      return next;
    });
  }, []);

  const updateSharedHeaderOverlay = useCallback((checked: boolean) => {
    setBuilderState((prev) => {
      const nextSections = (prev.sections ?? []).map((section) => {
        if (section.type !== 'header') return section;
        return {
          ...section,
          data: {
            ...section.data,
            backgroundOverlay: checked,
          },
        };
      });
      const next = { ...prev, sections: nextSections };
      saveToStorage(next);
      return next;
    });
  }, []);

  const handleBackgroundFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      compressImageFile(file)
        .then((imageUrl) => updatePageBackgroundUrl(imageUrl))
        .catch(() => {});
      e.target.value = '';
    },
    [updatePageBackgroundUrl],
  );

  useEffect(() => {
    if (initialized) return;
    setBuilderState(loadFromStorage());
    setInitialized(true);
  }, [initialized]);

  const page = useMemo(() => {
    if (!pageId) return null;
    return (builderState.pages ?? []).find((p) => p.id === pageId) ?? null;
  }, [builderState.pages, pageId]);

  const bodySections = useMemo(() => {
    if (!page) return [];
    return ensureBodySections(page).map((s, i) => ({ ...s, order: i }));
  }, [page]);

  useEffect(() => {
    if (!initialized) return;
    if (!page) return;
    if (Array.isArray(page.bodySections) && page.bodySections.length > 0)
      return;

    const nextPages = (builderState.pages ?? []).map((p) =>
      p.id === page.id ? { ...p, bodySections: ensureBodySections(p) } : p,
    );
    setBuilderState((prev) => {
      const next = { ...prev, pages: nextPages };
      saveToStorage(next);
      return next;
    });
  }, [builderState.pages, initialized, page]);

  useEffect(() => {
    if (!initialized || !page) return;
    if (page.id !== 'page-default-donate') return;
    const existing = Array.isArray(page.bodySections) ? page.bodySections : [];
    if (existing.length === 0) return;
    const needsVariantPatch = existing.some(
      (section) =>
        section.type === 'body' &&
        (section.data == null || section.data.variant !== 'donate'),
    );
    if (!needsVariantPatch) return;

    setBuilderState((prev) => {
      const nextPages = (prev.pages ?? []).map((p) => {
        if (p.id !== page.id) return p;
        const patchedSections = (p.bodySections ?? []).map((s) => {
          if (s.type !== 'body') return s;
          const existingData = s.data ?? {};
          return {
            ...s,
            data: {
              ...existingData,
              variant: 'donate',
              subtitle:
                typeof existingData.subtitle === 'string'
                  ? existingData.subtitle
                  : 'We need your help.',
              donateImageUrl:
                typeof existingData.donateImageUrl === 'string'
                  ? existingData.donateImageUrl
                  : '',
              donateSuggestedAmounts:
                typeof existingData.donateSuggestedAmounts === 'string'
                  ? existingData.donateSuggestedAmounts
                  : '100,250,500,1000',
              donateBankOptions:
                typeof existingData.donateBankOptions === 'string'
                  ? existingData.donateBankOptions
                  : 'GCash,BDO,UnionBank',
              donateMode:
                existingData.donateMode === 'monthly' ||
                existingData.donateMode === 'one-time'
                  ? existingData.donateMode
                  : 'one-time',
              donateButtonLabel:
                typeof existingData.donateButtonLabel === 'string'
                  ? existingData.donateButtonLabel
                  : 'Give Now',
              donateNote:
                typeof existingData.donateNote === 'string'
                  ? existingData.donateNote
                  : 'Secure online giving. Thank you for your generosity.',
              thankYouTitle:
                typeof existingData.thankYouTitle === 'string'
                  ? existingData.thankYouTitle
                  : 'Thank you for your gift!',
              thankYouSubtitle:
                typeof existingData.thankYouSubtitle === 'string'
                  ? existingData.thankYouSubtitle
                  : 'Your donation will help local wildlife thrive.',
              thankYouReceiptText:
                typeof existingData.thankYouReceiptText === 'string'
                  ? existingData.thankYouReceiptText
                  : "We've sent your donation receipt to [donor email address]",
              thankYouShareFacebookLabel:
                typeof existingData.thankYouShareFacebookLabel === 'string'
                  ? existingData.thankYouShareFacebookLabel
                  : 'Share on Facebook',
              thankYouShareInstagramLabel:
                typeof existingData.thankYouShareInstagramLabel === 'string'
                  ? existingData.thankYouShareInstagramLabel
                  : 'Share on Instagram',
            },
          };
        });
        return { ...p, bodySections: patchedSections };
      });
      const next = { ...prev, pages: nextPages };
      saveToStorage(next);
      return next;
    });
  }, [initialized, page]);

  useEffect(() => {
    if (!initialized) return;
    if (pageId && !page) {
      // If Give points to the default donate page and it does not exist yet
      // (older saved state), create it automatically instead of bouncing back.
      if (pageId === 'page-default-donate') {
        setBuilderState((prev) => {
          const currentPages = Array.isArray(prev.pages) ? prev.pages : [];
          if (currentPages.some((p) => p.id === pageId)) return prev;
          const donateSection = createSection('body', 0);
          donateSection.data = {
            ...donateSection.data,
            variant: 'donate',
            title: 'Support Our Ministry',
            subtitle: 'We need your help.',
            body: 'Your giving helps us continue outreach, discipleship, and community care.',
            donateImageUrl: '',
            donateSuggestedAmounts: '100,250,500,1000',
            donateBankOptions: 'GCash,BDO,UnionBank',
            donateMode: 'one-time',
            donateButtonLabel: 'Give Now',
            donateNote: 'Secure online giving. Thank you for your generosity.',
            thankYouTitle: 'Thank you for your gift!',
            thankYouSubtitle: 'Your donation will help local wildlife thrive.',
            thankYouReceiptText:
              "We've sent your donation receipt to [donor email address]",
            thankYouShareFacebookLabel: 'Share on Facebook',
            thankYouShareInstagramLabel: 'Share on Instagram',
          };
          const donatePage: WebsiteBuilderPage = {
            id: 'page-default-donate',
            title: 'Donate',
            slug: 'donate',
            bodySections: [donateSection],
          };
          const next = { ...prev, pages: [...currentPages, donatePage] };
          saveToStorage(next);
          return next;
        });
        return;
      }
      navigate(ROUTES.MEMBERSHIP.WEBSITE_BUILDER, { replace: true });
    }
  }, [initialized, navigate, page, pageId]);

  const updateBodySections = useCallback(
    (nextBody: BuilderSection[]) => {
      if (!page) return;
      setBuilderState((prev) => {
        const nextPages = (prev.pages ?? []).map((p) =>
          p.id === page.id ? { ...p, bodySections: nextBody } : p,
        );
        const next = { ...prev, pages: nextPages };
        saveToStorage(next);
        return next;
      });
    },
    [page],
  );

  const handleAddBodySection = useCallback(() => {
    const nextOrder =
      bodySections.length > 0
        ? Math.max(...bodySections.map((s) => s.order), 0) + 1
        : 0;
    const nextSection = createSection('body', nextOrder);
    if (nextSection.type === 'body') {
      nextSection.data = {
        ...nextSection.data,
        menuShowImageBanner: false,
      };
    }
    updateBodySections([...bodySections, nextSection]);
  }, [bodySections, updateBodySections]);

  const handleUpdateSectionData = useCallback(
    (id: string, data: Record<string, unknown>) => {
      updateBodySections(
        bodySections.map((s) => (s.id === id ? { ...s, data } : s)),
      );
    },
    [bodySections, updateBodySections],
  );

  const handleUpdateSectionLabel = useCallback(
    (id: string, label: string) => {
      updateBodySections(
        bodySections.map((s) =>
          s.id === id ? { ...s, customLabel: label } : s,
        ),
      );
    },
    [bodySections, updateBodySections],
  );

  const handleReorder = useCallback(
    (next: BuilderSection[]) => {
      updateBodySections(next.map((s, i) => ({ ...s, order: i })));
    },
    [updateBodySections],
  );

  const handleRemoveSection = useCallback(
    (id: string) => {
      updateBodySections(bodySections.filter((s) => s.id !== id));
      if (selectedSectionId === id) setSelectedSectionId(null);
    },
    [bodySections, selectedSectionId, updateBodySections],
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      resizeRef.current = { startX: e.clientX, startWidth: sidebarWidth };
      setResizingTarget('sidebar');
    },
    [sidebarWidth],
  );

  useEffect(() => {
    if (!resizingTarget) return;

    const handleMove = (e: MouseEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      const dx = e.clientX - r.startX;
      const next = clamp(r.startWidth + dx, SIDEBAR_MIN, SIDEBAR_MAX);
      setSidebarWidth(next);
    };

    const handleUp = () => {
      // Persist the latest sidebar width after resize ends.
      saveNumber(SIDEBAR_WIDTH_KEY, latestSidebarWidthRef.current);
      setResizingTarget(null);
      resizeRef.current = null;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizingTarget]);

  const { header, cta, footer } = useMemo(
    () => getSharedSections(builderState.sections ?? []),
    [builderState.sections],
  );

  const composedSections = useMemo(() => {
    const parts: BuilderSection[] = [];
    if (header) parts.push({ ...header, order: 0 });
    for (const s of bodySections) parts.push({ ...s, order: parts.length });
    if (cta) parts.push({ ...cta, order: parts.length });
    if (footer) parts.push({ ...footer, order: parts.length });
    return parts;
  }, [bodySections, cta, footer, header]);

  const hasCustomPageBackground = builderState.pageBackgroundUrl.trim() !== '';
  const effectivePageBackgroundUrl = hasCustomPageBackground
    ? builderState.pageBackgroundUrl
    : '';

  const sectionCount = composedSections.length;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.topBarTitle}>
          Page Builder{page?.title ? `: ${page.title}` : ''}
          <span className={styles.sectionCount}>({sectionCount} sections)</span>
        </div>
        <div className={styles.topBarActions}>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.MEMBERSHIP.WEBSITE_BUILDER)}
          >
            <ChevronLeft size={18} strokeWidth={2} />
            Back to Website Builder
          </Button>
          <Button
            type="button"
            variant={previewMode ? 'primary' : 'outline'}
            onClick={() => setPreviewMode((p) => !p)}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
        </div>
      </div>

      <div className={styles.mainLayout}>
        {!previewMode && (
          <>
            <aside
              className={styles.sidebar}
              style={{
                width: sidebarWidth,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                }}
              >
                {/* Page Background */}
                <div
                  style={{
                    marginBottom: '0.75rem',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
                    Page Background
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={builderState.pageBackgroundUrl}
                      onChange={(e) => updatePageBackgroundUrl(e.target.value)}
                      placeholder="Background image URL"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        border: '1px solid hsl(0 0% 82%)',
                        borderRadius: 8,
                        padding: '0.35rem 0.5rem',
                        fontSize: '0.85rem',
                        outline: 'none',
                      }}
                      aria-label="Page background image URL"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Browse
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => updatePageBackgroundUrl('')}
                      disabled={!builderState.pageBackgroundUrl}
                      title="Clear background"
                    >
                      Clear
                    </Button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleBackgroundFileChange}
                  />
                </div>

                <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
                  Body Sections
                </div>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    marginBottom: '0.65rem',
                    fontSize: '0.83rem',
                    color: 'hsl(215 16% 28%)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      header?.type === 'header'
                        ? ((header.data.backgroundOverlay as boolean) ?? false)
                        : false
                    }
                    onChange={(e) =>
                      updateSharedHeaderOverlay(e.target.checked)
                    }
                    disabled={!header}
                  />
                  Overlay header background
                </label>
                <div
                  style={{
                    color: 'hsl(215 16% 35%)',
                    fontSize: '0.85rem',
                    marginBottom: '0.75rem',
                  }}
                >
                  Header / CTA / Footer are shared from your main Website
                  Builder.
                </div>
                <Button type="button" onClick={handleAddBodySection}>
                  Add Body Section
                </Button>
                {!header || !cta || !footer ? (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      color: 'hsl(0 60% 40%)',
                      fontSize: '0.85rem',
                      flexShrink: 0,
                    }}
                  >
                    Missing shared Header/CTA/Footer. Add them in the main
                    Website Builder.
                  </div>
                ) : null}

                <div
                  style={{
                    marginTop: '0.75rem',
                    minHeight: 0,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <ActiveSectionsPanel
                    sections={bodySections}
                    selectedSectionId={selectedSectionId}
                    onSelectSection={setSelectedSectionId}
                    onUpdateSectionData={handleUpdateSectionData}
                    onUpdateSectionLabel={handleUpdateSectionLabel}
                    onReorder={handleReorder}
                    onRemoveSection={handleRemoveSection}
                  />
                </div>
              </div>
            </aside>

            <div
              className={styles.resizeHandle}
              onMouseDown={(e) => handleResizeStart(e)}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize body section library"
            />
          </>
        )}

        <main
          className={styles.canvasArea}
          style={
            hasCustomPageBackground
              ? {
                  backgroundImage: `url(${effectivePageBackgroundUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : {
                  backgroundImage: `linear-gradient(hsl(0 0% 35% / 0.55), hsl(0 0% 35% / 0.55)), url(${heroPlaceholder})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundBlendMode: 'saturation',
                }
          }
        >
          <Canvas
            sections={composedSections}
            previewMode={previewMode}
            onSelectSection={(id) => {
              if (!id) return;
              if (bodySections.some((s) => s.id === id))
                setSelectedSectionId(id);
            }}
            onUpdateSectionData={handleUpdateSectionData}
          />
        </main>
      </div>
    </div>
  );
}

export default PageBuilder;
